import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs/operators';
import { finalize } from 'rxjs';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzMessageService } from 'ng-zorro-antd/message';

import {
  ServicePackageService,
  ServicePackagePurchase,
  CancelOrRefundDto,
  RefundMethod,
  RefundPreview,
  RefundType,
  SettlementDay,
} from '@beautyapp/core';

/**
 * Refund / cancellation modal for a PARTIALLY-USED service package (beaut-290).
 *
 * The operator chooses a return TYPE (full price vs. remaining value) and a
 * METHOD (customer balance / no financial op / till expense). The exact amount
 * is computed by the BE via refundPreview — it is NEVER typed by hand — and is
 * refreshed whenever the type changes. A fully-used package cannot be refunded,
 * so the action is disabled when the preview reports `fullyUsed`.
 */
@Component({
  selector: 'beautyapp-package-refund-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzRadioModule,
    NzButtonModule,
    NzInputModule,
    NzSpinModule,
    NzDividerModule,
    NzAlertModule,
    TranslateModule,
  ],
  templateUrl: './package-refund-modal.component.html',
})
export class PackageRefundModalComponent {
  private modalRef = inject(NzModalRef);
  private packageService = inject(ServicePackageService);
  private message = inject(NzMessageService);
  private translate = inject(TranslateService);
  private data: { purchase: ServicePackagePurchase } = inject(NZ_MODAL_DATA);

  readonly purchase = this.data.purchase;

  loadingPreview = signal(false);
  submitting = signal(false);
  preview = signal<RefundPreview | null>(null);

  fullyUsed = computed(() => this.preview()?.fullyUsed === true);

  readonly today = new Date();

  /** The day the package was sold — the alternative till this can settle on. */
  readonly saleDate = this.purchase.purchasedAt ?? this.purchase.createdAt;

  /**
   * Only ask when the answer can differ.
   *
   * If the package was sold today, both options resolve to the same till and
   * the question is pure noise on the most common path — a mistake caught
   * immediately. The choice appears only once the sale is on an earlier day,
   * which is exactly when settling on today's drawer starts to misstate it.
   */
  readonly showSettlementChoice = this.isEarlierDay(this.saleDate);

  private isEarlierDay(iso: string | undefined): boolean {
    if (!iso) return false;
    const sale = new Date(iso);
    if (Number.isNaN(sale.getTime())) return false;
    const today = new Date();
    // Compare calendar days in the viewer's timezone, not elapsed hours: a sale
    // at 23:50 yesterday is a different till from one at 00:10 today even though
    // they are twenty minutes apart.
    return (
      sale.getFullYear() !== today.getFullYear() ||
      sale.getMonth() !== today.getMonth() ||
      sale.getDate() !== today.getDate()
    );
  }
  // Can't commit while a preview is in flight (amount unknown), while already
  // submitting, or when the package is fully used (refund is illegal).
  canSubmit = computed(
    () => !this.submitting() && !this.loadingPreview() && !this.fullyUsed()
  );

  form = new FormGroup({
    refundType: new FormControl<RefundType>('REMAINING', { nonNullable: true }),
    refundMethod: new FormControl<RefundMethod>('CASHBOX', {
      nonNullable: true,
    }),
    comment: new FormControl<string>('', { nonNullable: true }),
    // Which till day the money leaves. TODAY matches how the BE behaved before
    // this field existed, so the default changes nothing for the common case.
    settlementDay: new FormControl<SettlementDay>('TODAY', {
      nonNullable: true,
    }),
  });

  constructor() {
    // Amount depends only on the return type, so re-preview on every type change
    // (startWith fires the initial load). The method only routes the money.
    this.form.controls.refundType.valueChanges
      .pipe(
        startWith(this.form.controls.refundType.value),
        takeUntilDestroyed()
      )
      .subscribe((type) => this.loadPreview(type));
  }

  private loadPreview(refundType: RefundType): void {
    this.loadingPreview.set(true);
    this.packageService
      .refundPreview(this.purchase.id, refundType)
      .pipe(finalize(() => this.loadingPreview.set(false)))
      .subscribe({
        next: (p) => this.preview.set(p),
        error: (err) => this.showError(err),
      });
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.submitting.set(true);
    const v = this.form.getRawValue();
    const comment = v.comment.trim();
    const dto: CancelOrRefundDto = {
      refundType: v.refundType,
      refundMethod: v.refundMethod,
      ...(comment ? { comment } : {}),
      // Sent only when it differs from the BE default, so a same-day refund
      // posts exactly the payload it always did.
      ...(this.showSettlementChoice && v.settlementDay === 'SALE_DATE'
        ? { settlementDay: 'SALE_DATE' as const }
        : {}),
    };
    this.packageService
      .cancelOrRefund(this.purchase.id, dto)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: () => {
          this.message.success(
            this.translate.instant('servicePackages.messages.refunded')
          );
          this.modalRef.close(true);
        },
        error: (err) => this.showError(err),
      });
  }

  cancel(): void {
    this.modalRef.close(false);
  }

  private showError(err: { error?: { message?: string } }): void {
    this.message.error(
      err?.error?.message ||
        this.translate.instant('servicePackages.messages.cancelError')
    );
  }
}
